/*
  ORQUIS motion. Two unrelated jobs, both gated on prefers-reduced-motion.

  1. Scroll linked depth for the homepage bands:

       data-orquis-parallax   the element drifts vertically as its scene crosses
                              the viewport. Travel is set with
                              data-parallax-amount (percent of its own height,
                              default 8).

       .orquis-zoom           the element scales down to 1 as its scene crosses
                              the viewport, from ORQUIS_ZOOM_FROM below. This one
                              is a class rather than a data attribute because it
                              is applied by Liquid's image_tag filter, whose
                              named arguments cannot carry hyphens.

     Both follow the nearest [data-orquis-scene] ancestor, so a photograph moves
     while the panel around it stays put. Driven by ViewTimeline rather than a
     scroll listener: the animation runs on the compositor, so it stays glued to
     the scroll position even under load. On engines without ViewTimeline
     nothing happens at all, and every layout here is correct without the
     flourish, so there is no fallback to maintain.

     Timelines are built lazily through IntersectionObserver: constructed at
     page load they snapshot unsettled layout and report garbage progress (the
     same trap the zoom out flourish in theme.liquid hit).

  2. The seam marquee's loop. See seam() below.
*/
(function () {
  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- scroll linked depth ---------------- */

  var ORQUIS_ZOOM_FROM = 1.18;

  function attach(el) {
    var scene = el.closest('[data-orquis-scene]') || el.parentElement;
    if (!scene) return;

    var keyframes;
    /* Two ways to spend the same 18%. The default runs across the whole cover
       range: the picture arrives enlarged and settles to 1 as its scene crosses
       the screen. That is wrong for anything resting at the top of the page,
       which is already mid-range at scroll 0 and so loads visibly zoomed.

       .orquis-zoom--exit spends the same scale on the exit range instead, for
       anything resting at the top of the page. It rests ENLARGED — the CSS gives
       it scale(ORQUIS_ZOOM_FROM) as its baseline, so the first frame of this
       animation is exactly what is already painted and nothing moves on load —
       then shrinks back to 1 as the scene leaves, and swells again on the way
       up. Scaling below 1 is not available: these photographs are object-fit
       cover at exactly 100%, so anything under 1 pulls their edges off the
       frame. Resting oversized is what buys the room to zoom out.
       If ORQUIS_ZOOM_FROM changes, --orquis-zoom-from in orquis-home.css has to
       change with it or the hero will jump on load.
       (A class, not a data attribute, for the same reason .orquis-zoom is one:
       image_tag's named arguments cannot carry hyphens.) */
    var onExit = el.classList.contains('orquis-zoom--exit');
    if (el.classList.contains('orquis-zoom')) {
      keyframes = { transform: ['scale(' + ORQUIS_ZOOM_FROM + ')', 'scale(1)'] };
    } else {
      var amount = parseFloat(el.dataset.parallaxAmount || '8');
      keyframes = {
        transform: ['translate3d(0, ' + -amount + '%, 0)', 'translate3d(0, ' + amount + '%, 0)'],
      };
    }

    try {
      el.animate(keyframes, {
        fill: 'both',
        easing: 'linear',
        timeline: new ViewTimeline({ subject: scene }),
        rangeStart: onExit ? 'exit 0%' : 'cover 0%',
        rangeEnd: onExit ? 'exit 100%' : 'cover 100%',
      });
    } catch (e) {
      /* older engine, skip the flourish */
    }
  }

  function initDepth(root) {
    if (reduced || !('ViewTimeline' in window)) return;

    var targets = (root || document).querySelectorAll('[data-orquis-parallax], .orquis-zoom');
    if (!targets.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          attach(entry.target);
        });
      },
      { rootMargin: '50% 0%' }
    );

    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------- seam marquee ----------------

     A two copy marquee that slides by -50% only loops seamlessly when one copy
     is at least as wide as the viewport. Ours is not: four short promises come
     to roughly a thousand pixels, so on a wide screen the track ran out and the
     band went blank until the loop restarted.

     So the track is measured rather than guessed. One list is the unit; enough
     copies are cloned to cover the viewport plus one whole unit, and the
     animation shifts by exactly one unit's width in pixels. Whatever the
     viewport, whatever the copy, there is always another list queued in the gap
     the shift opens.

     Re-run on resize because the number of copies depends on the viewport.
  */
  function seam(el) {
    var viewport = el.querySelector('.orquis-seam__viewport');
    var track = el.querySelector('.orquis-seam__track');
    if (!viewport || !track) return;

    var lists = track.querySelectorAll('.orquis-seam__list');
    if (!lists.length) return;

    // Drop any copies from a previous pass before measuring, or each resize
    // would measure a track that already carries the last run's clones.
    for (var i = lists.length - 1; i >= 1; i--) {
      track.removeChild(lists[i]);
    }

    var unit = lists[0];
    var width = unit.getBoundingClientRect().width;
    if (!width) return;

    var copies = Math.ceil(viewport.getBoundingClientRect().width / width) + 1;
    for (var j = 0; j < copies; j++) {
      var clone = unit.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    el.style.setProperty('--orquis-seam-shift', '-' + width + 'px');
  }

  function initSeam(root) {
    if (reduced) return;
    (root || document).querySelectorAll('.orquis-seam').forEach(seam);
  }

  /* ---------------- wiring ---------------- */

  var resizeTimer;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      initSeam(document);
    }, 200);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initDepth(document);
    initSeam(document);
    window.addEventListener('resize', onResize);
  });

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    initDepth(event.target);
    initSeam(event.target);
  });
})();
